<template>
  <div
    class="bg-[#FFFFFF] flex flex-col"
    style="font-family: 'Poppins', sans-serif;"
  >
    <!-- Header -->
    <header class="relative group flex items-center justify-between p-[0.5rem] bg-white border-b border-gray-200">
      <div class="flex flex-col items-center justify-center grow p-[8px] relative">
        <div class="flex flex-col items-center justify-center gap-[0.5rem]">
          <div class="flex items-center justify-center gap-[0.5rem]">
            <input
              v-model="articleTitle"
              placeholder="Masukkan Judul Artikel"
              class="text-[18px] font-semibold text-[#1F2937] mb-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 p-0 m-0 placeholder-gray-400 text-center"
              :size="articleTitle ? articleTitle.length : 22"
              :maxlength="config.MAX_TITLE_LENGTH"
              @input="handleTitleInput"
              @blur="handleTitleBlur"
            >
          </div>
          <div
            v-if="titleError"
            class="text-[#F03D3D] text-sm font-medium text-center"
          >
            {{ titleError }}
          </div>
        </div>
      </div>
      <div class="flex items-center justify-center gap-[8px] absolute right-[24px]">
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-class="opacity-0 scale-90 -translate-y-[2px]"
          enter-from-class="opacity-0 scale-90 -translate-y-[2px]"
          leave-active-class="transition duration-200 ease-out"
          leave-to-class="opacity-0 scale-90 -translate-y-[2px]"
        >
          <div
            v-if="articleTitle.length >= (config.MAX_TITLE_LENGTH - 20)"
            class="bg-[#FEF2F2] text-[#DC2626] text-[12px] px-[10px] py-[2px] rounded-[10px] opacity-80 group-focus-within:opacity-90 group-hover:opacity-20 hover:opacity-100 transition-opacity duration-300"
          >
            <span class="font-medium">{{ articleTitle.length }}/</span>{{ config.MAX_TITLE_LENGTH }}
          </div>
        </transition>
        <button
          class="text-gray-400 hover:text-gray-600 focus:outline-none"
          @click="goBack"
        >
          <XIcon size="20" />
        </button>
      </div>
    </header>

    <!-- Main Content Area (Split Pane) -->
    <main class="grid sm:grid-cols-2 p-[24px] gap-[24px] sm:h-[75vh]">

      <!-- Left Pane: Editor -->
      <div class="flex-1 flex flex-col bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden h-[70vh] sm:h-full">
        <div class="flex items-center justify-around flex-wrap gap-1 py-[0.625rem] border border-gray-200 rounded-t-md bg-white">
          <div
            v-for="(group, i) in toolbarConfig"
            :key="'div-'+i"
            class="bg-white flex items-center justify-center rounded-[8px] border-[1px] border-[#E5E5E5]"
            style="box-shadow: 0px 1px 2px 0px #0000001A;"
          >
            <button
              v-for="(item, j) in group"
              :key="j"
              class="flex items-center justify-center w-[34px] h-[36px] border-r-[1px] border-[#E5E5E5] last:border-r-0 first:rounded-l-[8px] last:rounded-r-[8px] hover:bg-gray-100 transition-colors focus:outline-none"
              @click="item.image ? triggerImageUpload() : insertSyntax(item.pre, item.suf, item.isBlock)"
            >
              <component
                :is="item.icon"
                v-if="item.icon"
                size="16"
                class="text-gray-700"
              />
              <img
                v-else-if="item.img"
                :src="item.img"
                :alt="item.label"
                class="size-[16px] text-gray-700"
                :class="item.cls"
              >
              <span
                v-else
                class="font-bold text-gray-700"
                :class="item.cls"
              >{{ item.label }}</span>
            </button>
          </div>
        </div>

        <input
          ref="imageInput"
          type="file"
          accept="image/*"
          class="hidden"
          @change="handleFileUpload"
        >

        <div class="flex-1 overflow-auto bg-white">
          <EditorTextarea
            ref="textareaComponent"
            v-model="content"
            placeholder="Tulis artikel markdown di sini..."
            @image-pasted="handleImagePasted"
          />
        </div>
      </div>

      <!-- Right Pane: Preview -->
      <div class="flex-1 flex flex-col bg-[#F5F5F5] rounded-md shadow-sm border border-gray-200 overflow-hidden h-full">
        <!-- Preview Header -->
        <div class="flex items-center justify-between p-[1rem] pb-0">
          <div class="bg-[#171717] text-white text-[14px] py-[0.45rem] px-[0.75rem] rounded-full">
            Preview
          </div>
          <div class="text-14px text-[#0A0A0A]">
            Last Update: {{ lastUpdateText }}
          </div>
        </div>

        <!-- Preview Content -->
        <div class="flex-1 overflow-auto m-[1rem] p-[1rem] rounded-[12px] shadow-sm bg-white text-[#0A0A0A]">
          <h1
            class="text-3xl font-bold mb-[0.5rem] text-[#0A0A0A]"
            style="overflow-wrap: anywhere;"
          >
            {{ articleTitle }}
          </h1>
          <p class="text-sm mb-8">
            Terakhir Diupdate: {{ formattedDate }}
          </p>
          <EditorPreview :content="previewContent" />
        </div>
      </div>

    </main>

    <!-- Footer / Action bar -->
    <footer class="flex items-center justify-between flex-wrap gap-[8px] px-[1.5rem] bg-white pb-[8px]">
      <div class="max-w-[430px] min-w-[200px]">
        <InfiniteSelect
          v-model="selectedCategory"
          class="category-select shadow-xs !rounded-[8px]"
          :options="categoryOptions"
          :loading="categoryLoading"
          option-label="name"
          :reduce="option => option.id"
          placeholder="Pilih Kategori"
          :clearable="false"
        >
          <template #selectedOption="{ value }">
            <span class="truncate block w-full min-w-0">{{ value.name }}</span>
          </template>
          <template #optionValue="{ value }">
            <div class="flex items-center justify-between w-full min-w-0">
              <span>{{ value.name }}</span>
              <CheckIcon
                v-if="selectedCategory === value.id"
                size="16"
              />
            </div>
          </template>
        </InfiniteSelect>
      </div>

      <div class="flex items-center gap-[8px] flex-wrap">
        <button
          v-if="$route.query.id"
          class="!p-0 h-[36px] w-[126px] !rounded-[8px] text-[#F03D3D] text-sm font-medium hover:underline focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:no-underline"
          :disabled="isEmpty || isMutating"
          @click="submitArticle('hide')"
        >
          {{ mutating.hide ? 'Menyembunyikan...' : 'Sembunyikan' }}
        </button>
        <button
          class="!p-0 h-[36px] w-[126px] !rounded-[8px] border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isEmpty || isMutating"
          @click="submitArticle('draft')"
        >
          {{ mutating.draft ? 'Menyimpan...' : 'Simpan ke Draft' }}
        </button>
        <button
          class="!p-0 h-[36px] w-[126px] !rounded-[8px] text-sm font-medium text-white bg-[#F97316] hover:bg-[#ea580c] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isEmpty || isMutating"
          @click="submitArticle('publish')"
        >
          {{ mutating.publish ? 'Mem-publish...' : 'Publish' }}
        </button>
      </div>
    </footer>

    <EditorUnsaveDialog
      ref="editorUnsaveDialog"
      :disable-draft="isEmpty"
    />
  </div>
</template>

<script>
import {
  XIcon, Edit2Icon, BoldIcon, ItalicIcon, UnderlineIcon,
  ListIcon, CodeIcon, GridIcon, ImageIcon, LinkIcon, CheckIcon,
} from 'vue-feather-icons'
import moment from 'moment'
import { DAY_MONTH_YEAR } from '@/libs/filterDate'
import InfiniteSelect from '@/views/components/select/InfiniteSelect.vue'
import EditorTextarea from './EditorTextarea.vue'
import EditorPreview from './EditorPreview.vue'
import EditorUnsaveDialog from './EditorUnsaveDialog.vue'
import { toolbarConfig, ARTICLE_SUBMIT_MESSAGE } from './config'

export default {
  name: 'ArticleEditorPage',
  components: {
    XIcon,
    Edit2Icon,
    BoldIcon,
    ItalicIcon,
    UnderlineIcon,
    ListIcon,
    CodeIcon,
    GridIcon,
    ImageIcon,
    LinkIcon,
    CheckIcon,
    EditorTextarea,
    EditorPreview,
    InfiniteSelect,
    EditorUnsaveDialog,
  },
  data() {
    return {
      initialTitle: '',
      initialContent: '',
      articleTitle: '',
      content: '',
      /** @type {Array<{id: string, file: File, tempUrl: string, realUrl?: string}>} */
      pendingImages: [],
      lastUpdateText: 'Just now',
      formattedDate: DAY_MONTH_YEAR(new Date()),
      lastUpdatedAt: null,
      updateInterval: null,
      toolbarConfig,
      isHiding: false,
      isDrafting: false,
      isPublishing: false,
      categoryOptions: [],
      categoryLoading: false,
      selectedCategory: null,
      mutating: {
        hide: false,
        draft: false,
        publish: false,
      },
      titleError: null,
    }
  },
  computed: {
    config() {
      return {
        MAX_TITLE_LENGTH: 100,
      }
    },
    hasChanges() {
      return this.articleTitle !== this.initialTitle || this.content !== this.initialContent
    },
    previewContent() {
      if (!this.pendingImages.length) return this.content
      const map = new Map(this.pendingImages.map(img => [`local-image:${img.id}`, img.tempUrl]))
      return this.content.replace(/local-image:[a-zA-Z0-9-]+/g, match => map.get(match) || match)
    },
    isMutating() {
      return Object.values(this.mutating).some(Boolean)
    },
    isEmpty() {
      return !this.articleTitle?.trim() || !this.content?.trim()
    },
  },
  created() {
    this.initialTitle = this.articleTitle
    this.initialContent = this.content
    this.fetchCategories()
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    if (this.$route.query.id) {
      this.fetchArticleDetail(this.$route.query.id)
    }

    this.updateInterval = setInterval(this.updateLastUpdateText, 60000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
    window.removeEventListener('beforeunload', this.handleBeforeUnload)

    /** Cleanup temporary URLs if component is destroyed before saving */
    this.pendingImages.forEach(img => {
      URL.revokeObjectURL(img.tempUrl)
    })
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasChanges) {
      this.$refs.editorUnsaveDialog.show({
        onSubmit: () => {
          next()
        },
        onToDraft: async () => {
          await this.submitArticle('draft')
          next()
        },
        onCancel: () => {
          next(false)
        },
      })
    } else {
      next()
    }
  },
  methods: {
    /**
     * Updates the text displaying how long ago the article was last updated.
     */
    updateLastUpdateText() {
      if (this.lastUpdatedAt) {
        this.lastUpdateText = moment(this.lastUpdatedAt).locale('en').fromNow()
      }
    },
    /**
     * Handles input for the article title, validating against disallowed characters.
     * @param {Event} event - The input event.
     */
    handleTitleInput(event) {
      const { value } = event.target
      const regex = /[^a-zA-Z0-9\s.,?!\-/()]/g
      const cleanValue = value.replace(regex, '')
      const trimmedValue = cleanValue.trimStart()

      if (cleanValue !== value) {
        this.titleError = 'Gunakan huruf, angka, atau tanda baca standar.'
        this.articleTitle = trimmedValue
      } else {
        if (this.titleError) this.titleError = null
        this.articleTitle = trimmedValue
      }
    },
    /**
     * Trims leading and trailing whitespace when the title input loses focus.
     */
    handleTitleBlur() {
      this.articleTitle = this.articleTitle.trim()
    },
    /**
     * Prevents window unload if there are unsaved changes.
     * @param {Event} e - The beforeunload event.
     */
    handleBeforeUnload(e) {
      if (this.hasChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    /**
     * Fetches article details by ID and populates the component state.
     * @param {string|number} id - The article ID.
     */
    async fetchArticleDetail(id) {
      try {
        const response = await this.$http_new.get(`/live-chat/api/v1/articles/${id}`)
        const { data } = response.data
        if (data) {
          this.articleTitle = data.title || ''
          this.content = data.content || ''
          this.selectedCategory = data.category_id || null
          this.formattedDate = DAY_MONTH_YEAR(data.updated_at)
          this.lastUpdatedAt = data.updated_at
          this.updateLastUpdateText()

          this.initialTitle = this.articleTitle
          this.initialContent = this.content
        }
      } catch (error) {
        this.$toast_error({ message: error?.response?.data?.meta?.message || 'Gagal memuat artikel' })
      }
    },
    /**
     * Fetches available categories for the article.
     */
    async fetchCategories() {
      this.categoryLoading = true
      try {
        const { data } = await this.$http_new.get('/live-chat/api/v1/categories/options')
        if (data && data.data) {
          this.categoryOptions = data.data
          if (!this.selectedCategory && !this.$route.query.id) {
            const tanpaKategori = this.categoryOptions.find(c => c.name === 'Tanpa Kategori')
            if (tanpaKategori) {
              this.selectedCategory = tanpaKategori.id
            }
          }
        }
      } catch (error) {
        this.$toast_error({ message: 'Gagal mengambil kategori, silahkan reload halaman untuk mencoba lagi' })
      } finally {
        this.categoryLoading = false
      }
    },
    /**
     * Navigates back to the previous route.
     */
    goBack() {
      this.$router.back()
    },
    /**
     * Inserts markdown syntax into the editor.
     * @param {string} prefix - The syntax prefix.
     * @param {string} [suffix=''] - The syntax suffix.
     * @param {boolean} [isBlock=false] - Whether it is a block element.
     */
    insertSyntax(prefix, suffix = '', isBlock = false) {
      this.$refs.textareaComponent.insertText(prefix, suffix, isBlock)
    },
    /**
     * Triggers the hidden image file input.
     */
    triggerImageUpload() {
      this.$refs.imageInput.click()
    },
    /**
     * Handles file selection from the input element.
     * @param {Event} event - The change event.
     */
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.handleImagePasted(file)
      }
      // eslint-disable-next-line no-param-reassign
      event.target.value = ''
    },
    /**
     * Processes a pasted or selected image file, validating format/size and generating a temporary URL.
     * @param {File} file - The image file to process.
     */
    handleImagePasted(file) {
      const validExtensions = /\.(jpg|jpeg|png|webp|heif|heic)$/i
      const validMimeTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heif', 'image/heic']

      if (!validExtensions.test(file.name) && !validMimeTypes.includes(file.type)) {
        this.$toast_error({ message: 'Gagal memuat: Format tidak didukung' })
        return
      }

      const maxSize = 5 * 1024 * 1024
      if (file.size > maxSize) {
        this.$toast_error({ message: 'Gagal memuat: Ukuran maksimal 5 MB' })
        return
      }

      const tempUrl = URL.createObjectURL(file)
      const id = `${Date.now().toString()}-${Math.random().toString(36).substr(2, 5)}`

      this.pendingImages.push({ id, file, tempUrl })

      const isTable = this.$refs.textareaComponent?.isCursorInTable()
      this.insertSyntax(isTable ? `local-image:${id}` : `![${file.name}](local-image:${id})`, '')
    },
    /**
     * Uploads pending images to the server and updates the markdown content with real URLs.
     * @returns {Promise<string>} The updated markdown content.
     */
    async uploadPendingImages() {
      if (!this.pendingImages.length) return this.content

      const presentKeys = new Set(this.content.match(/local-image:[a-zA-Z0-9-]+/g) || [])

      const activeImages = []
      this.pendingImages.forEach(img => {
        if (presentKeys.has(`local-image:${img.id}`)) {
          activeImages.push(img)
        } else {
          URL.revokeObjectURL(img.tempUrl)
        }
      })
      this.pendingImages = activeImages

      if (!this.pendingImages.length) return this.content

      const results = await Promise.all(this.pendingImages.map(async img => {
        try {
          const formData = new FormData()
          formData.append('image', img.file)

          const { data } = await this.$http_new.post('/live-chat/api/v1/articles/images', formData)
          return { ...img, realUrl: data.data.url }
        } catch (e) {
          throw new Error('Gagal mengunggah gambar, silakan coba lagi')
        }
      }))

      const urlMap = new Map(results.map(({ id, tempUrl, realUrl }) => {
        URL.revokeObjectURL(tempUrl)
        return [`local-image:${id}`, realUrl]
      }))

      this.content = this.content.replace(/local-image:[a-zA-Z0-9-]+/g, match => urlMap.get(match) || match)

      this.pendingImages = []
      return this.content
    },
    /**
     * Submits the article to the server (draft, publish, or hide).
     * @param {'draft'|'publish'|'hide'} status - The submission status.
     */
    async submitArticle(status) {
      if (this.isEmpty || this.isMutating) return

      this.$set(this.mutating, status, true)

      try {
        const finalContent = await this.uploadPendingImages()
        this.articleTitle = this.articleTitle.trim()
        const payload = {
          title: this.articleTitle,
          content: finalContent,
          category_id: this.selectedCategory,
          status,
        }

        if (this.$route.query.id) {
          await this.$http_new.put(`/live-chat/api/v1/articles/${this.$route.query.id}`, payload)
        } else {
          await this.$http_new.post('/live-chat/api/v1/articles', payload)
        }

        this.initialTitle = this.articleTitle
        this.initialContent = this.content

        const successMessage = ARTICLE_SUBMIT_MESSAGE.success[status] ?? 'Proses berhasil'
        this.$toast_success({ message: successMessage })

        this.$router.push({ name: 'PusatBantuan', query: { tab: 'article' } })
      } catch (error) {
        const defaultErrorMessage = ARTICLE_SUBMIT_MESSAGE.failed[status] ?? 'Proses gagal, silakan coba lagi'
        let backendMessage = error?.response?.data?.meta?.message

        if (backendMessage === 'article with this title already exists') {
          const truncatedTitle = this.articleTitle.length > 30 ? `${this.articleTitle.substring(0, 26)}...` : this.articleTitle
          backendMessage = `Judul artikel "${truncatedTitle}" sudah digunakan, silakan gunakan judul yang berbeda.`
        }

        this.$toast_error({ message: backendMessage || defaultErrorMessage })
      } finally {
        this.$set(this.mutating, status, false)
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
::v-deep .category-select .vs__dropdown-menu {
  top: auto !important;
  bottom: calc(100% + 4px) !important;
  border-radius: 8px !important;
  padding: 4px;
}

::v-deep .category-select .vs__dropdown-option {
  padding: 8px 12px;
  border-radius: 4px;
  color: #0A0A0A;
  overflow: auto;
}

::v-deep .category-select .vs__dropdown-option::after {
  display: none;
}

::v-deep .vs__selected-options{
  flex-wrap: nowrap;
}

::v-deep .category-select .vs__dropdown-option--selected {
  background-color: #FCEFE5 !important;
  color: #0A0A0A !important;
  font-weight: 400;
}

/* Fix width shrinking when dropdown opens */
::v-deep .category-select.vs--single.vs--open .vs__selected {
  position: relative !important;
}
::v-deep .category-select.vs--single.vs--open .vs__search {
  position: absolute !important;
  left: 0;
  top: 0;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 7px !important;
}
</style>
